import { Check, X as CloseIcon } from "@tamagui/lucide-icons";
import { Platform } from "react-native";
import { Sheet, XStack, YStack, useMedia } from "tamagui";

import { Adapt } from "@/components";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Header } from "@/components/Layout";
import { H1, Paragraph } from "@/components/Typography";
import { brandName } from "@/config/brand";

interface DeleteDialogProps {
  showDeleteDialog: boolean;
  handleConfirmDelete: () => void;
  handleDialogOpenChange: () => void;
  handleClearDeletePayload: () => void;
  isDeleting: boolean;
}

export default function RevokeConsentDialog({
  showDeleteDialog,
  handleConfirmDelete,
  handleDialogOpenChange,
  handleClearDeletePayload,
  isDeleting,
}: DeleteDialogProps) {
  const { lg } = useMedia();

  return (
    <Dialog modal open={showDeleteDialog} onOpenChange={handleDialogOpenChange}>
      <Dialog.Adapt when={!lg}>
        <Sheet
          modal
          dismissOnSnapToBottom
          snapPointsMode="fit"
          moveOnKeyboardChange
        >
          <Sheet.Frame>
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            style={{
              backdropFilter: "blur(8px)",
            }}
          />
        </Sheet>
      </Dialog.Adapt>

      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          style={Platform.select({
            web: {
              backdropFilter: "blur(8px)",
            },
          })}
        />
        <Dialog.FocusScope trapped loop focusOnIdle={300}>
          <Dialog.Content padding={0} width={500}>
            <YStack
              padding="$200"
              width="100%"
              height="100%"
              gap="$100"
              borderTopLeftRadius="$150"
              borderTopRightRadius="$150"
              borderBottomLeftRadius="$150"
              borderBottomRightRadius="$150"
              flex={1}
            >
              <XStack
                justifyContent="flex-end"
                alignItems="center"
                marginBottom="$100"
              >
                <Dialog.Close asChild displayWhenAdapted>
                  <Button.Wrapper aria-label="Close">
                    <CloseIcon />
                  </Button.Wrapper>
                </Dialog.Close>
              </XStack>
              <YStack gap="$200">
                <Header>
                  <Dialog.Title asChild>
                    <H1 size="$lg" $lg={{ size: "$lg" }}>
                      Delete data
                    </H1>
                  </Dialog.Title>
                </Header>
                <Paragraph color="$textSecondary">
                  If you delete this, {brandName} will forget all the info and
                  connections it built from it — and there’s no way to bring it
                  back.
                </Paragraph>
              </YStack>
              <XStack justifyContent="flex-end" marginTop="$200">
                <Button
                  mode="tertiary"
                  disabled={isDeleting}
                  onPress={() => {
                    handleClearDeletePayload();
                    handleDialogOpenChange();
                  }}
                >
                  No, cancel
                </Button>
                <Button
                  mode="primary"
                  loading={isDeleting}
                  icon={<Check />}
                  onPress={() => {
                    // confirmation handles dialog close after success
                    handleConfirmDelete();
                  }}
                  disabled={isDeleting}
                >
                  Continue
                </Button>
              </XStack>
            </YStack>
          </Dialog.Content>
        </Dialog.FocusScope>
      </Dialog.Portal>
    </Dialog>
  );
}
